import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, Clock, MapPin, AlertTriangle, CheckCircle2, XCircle, Users, Wrench, Shield, Send, History, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RIESGO_COLORS = { BAJO: 'bg-green-100 text-green-700', MEDIO: 'bg-yellow-100 text-yellow-700', ALTO: 'bg-orange-100 text-orange-700', CRITICO: 'bg-red-100 text-red-700' };
const RIESGO_BG = { BAJO: 'bg-green-500', MEDIO: 'bg-yellow-500', ALTO: 'bg-orange-500', CRITICO: 'bg-red-500' };
const ESTADO_COLORS = { EN_VALIDACION: 'bg-blue-50 text-blue-700 border border-blue-200', PEND_APROBACION: 'bg-purple-50 text-purple-700 border border-purple-200', APROBADA: 'bg-emerald-50 text-emerald-700 border border-emerald-200', RECHAZADA: 'bg-red-50 text-red-700 border border-red-200', CERRADA: 'bg-slate-100 text-slate-700 border border-slate-300' };
const ESTADO_LABELS = { EN_VALIDACION: 'EN VALIDACION', PEND_APROBACION: 'PEND. APROBACION', APROBADA: 'APROBADA', RECHAZADA: 'RECHAZADA', CERRADA: 'CERRADA' };
const SEM_COLORS = { OK: 'bg-emerald-500', ALERTA: 'bg-amber-500', CRITICO: 'bg-red-500' };

export default function ActividadDetalle() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [avanceEdit, setAvanceEdit] = useState(false);
  const [avancePct, setAvancePct] = useState(0);
  const [avanceComentario, setAvanceComentario] = useState('');
  const [showCerrar, setShowCerrar] = useState(false);
  const [cerrarComentario, setCerrarComentario] = useState('');

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/actividades-operacionales/${id}`, { headers });
      setA(res.data);
    } catch { navigate('/planificador/actividades'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [id, token]);

  const handleEnviarAprobacion = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/actividades-operacionales/${id}/enviar-aprobacion`, {}, { headers });
      toast.success('Actividad enviada a aprobacion');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setSending(false);
  };

  const handleUpdateAvance = async () => {
    try {
      await axios.put(`${API}/actividades-operacionales/${id}/avance`, { porcentaje_avance: avancePct, comentario_avance: avanceComentario }, { headers });
      toast.success('Avance actualizado');
      setAvanceEdit(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleCerrar = async () => {
    try {
      await axios.put(`${API}/actividades-operacionales/${id}/cerrar`, { comentario: cerrarComentario }, { headers });
      toast.success('Actividad cerrada');
      setShowCerrar(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  useEffect(() => {
    if (a) {
      setAvancePct(a.porcentaje_avance || 0);
      setAvanceComentario(a.comentario_avance || '');
    }
  }, [a]);

  if (loading || !a) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#a05e49]" /></div>;

  const hasHighRisks = a.riesgos?.some(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO');
  const ssoAnswered = a.preguntas_sso?.filter(q => q.respuesta).length || 0;
  const ssoTotal = a.preguntas_sso?.length || 0;

  return (
    <div className="space-y-6" data-testid="actividad-detalle">
      <button onClick={() => navigate('/planificador/actividades')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="btn-volver">
        <ArrowLeft className="w-4 h-4" />Volver a actividades
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="text-sm font-mono text-[#a05e49]">{a.codigo}</span>
            <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded ${ESTADO_COLORS[a.estado] || ''}`}>{ESTADO_LABELS[a.estado] || a.estado}</span>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${RIESGO_COLORS[a.nivel_riesgo] || ''}`}>{a.nivel_riesgo}</span>
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">{a.tipo_tarea}</span>
          </div>
          <h1 className="text-xl font-bold text-slate-900">{a.titulo}</h1>
          {a.descripcion && <p className="text-sm text-slate-500 mt-1">{a.descripcion}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {a.estado !== 'CERRADA' && (
            <button onClick={() => setShowCerrar(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
              data-testid="btn-cerrar">
              <Check className="w-4 h-4" /> Cerrar Actividad
            </button>
          )}
          {(a.estado === 'EN_VALIDACION' || a.estado === 'RECHAZADA') && (
            <button onClick={handleEnviarAprobacion} disabled={sending}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              data-testid="btn-enviar-aprobacion">
              <Send className="w-4 h-4" />{sending ? 'Enviando...' : 'Enviar a Aprobacion'}
            </button>
          )}
        </div>
      </div>

      {/* Avance */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="seccion-avance">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Avance de la Actividad</h3>
          {a.estado !== 'CERRADA' && !avanceEdit && (
            <button onClick={() => setAvanceEdit(true)} className="text-xs text-[#00827F] hover:underline font-medium" data-testid="btn-editar-avance">Editar avance</button>
          )}
        </div>
        <div className="flex items-center gap-4 mb-2">
          <div className="flex-1">
            <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${a.porcentaje_avance || 0}%`, backgroundColor: a.estado === 'CERRADA' ? '#059669' : '#00827F' }} />
            </div>
          </div>
          <span className="text-lg font-bold text-slate-900 w-14 text-right">{a.porcentaje_avance || 0}%</span>
        </div>
        {a.comentario_avance && <p className="text-xs text-slate-500 mt-1">{a.comentario_avance}</p>}
        {a.fecha_cierre && <p className="text-xs text-emerald-600 font-medium mt-2">Cerrada el {a.fecha_cierre.slice(0,10).split('-').reverse().join('-')}</p>}

        {avanceEdit && a.estado !== 'CERRADA' && (
          <div className="mt-4 border-t pt-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Porcentaje de Avance</label>
              <div className="flex items-center gap-3">
                <input type="range" min="0" max="100" step="5" value={avancePct} onChange={e => setAvancePct(parseInt(e.target.value))}
                  className="flex-1 accent-[#00827F]" data-testid="input-avance-pct" />
                <span className="text-sm font-bold text-slate-700 w-12 text-right">{avancePct}%</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Comentario</label>
              <textarea value={avanceComentario} onChange={e => setAvanceComentario(e.target.value)} rows={2}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Descripcion del avance..." data-testid="input-avance-comentario" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleUpdateAvance} className="px-4 py-2 bg-[#00827F] text-white text-sm font-medium rounded-lg hover:bg-[#006663]" data-testid="btn-guardar-avance">Guardar Avance</button>
              <button onClick={() => setAvanceEdit(false)} className="px-4 py-2 border text-sm rounded-lg text-slate-600 hover:bg-slate-50">Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Cerrar */}
      {showCerrar && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowCerrar(false)}>
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-testid="modal-cerrar">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Cerrar Actividad</h3>
            <p className="text-sm text-slate-500 mb-4">El avance se marcara como 100% y no se podra modificar.</p>
            <textarea value={cerrarComentario} onChange={e => setCerrarComentario(e.target.value)} rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm mb-4" placeholder="Comentario de cierre (opcional)..." data-testid="input-cerrar-comentario" />
            <div className="flex gap-2">
              <button onClick={handleCerrar} className="flex-1 py-2.5 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800" data-testid="btn-confirmar-cerrar">Confirmar Cierre</button>
              <button onClick={() => setShowCerrar(false)} className="flex-1 py-2.5 border text-sm rounded-lg text-slate-600 hover:bg-slate-50">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Semaforos */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-slate-500" /><h3 className="text-sm font-semibold text-slate-700">Semaforos de Cumplimiento</h3></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'PERSONAL', icon: Users, ok: (a.personal?.length || 0) > 0 },
            { label: 'RECURSOS', icon: Wrench, ok: (a.recursos?.length || 0) > 0 },
            { label: 'RIESGOS', icon: AlertTriangle, ok: !hasHighRisks },
            { label: 'GENERAL', icon: CheckCircle2, ok: a.semaforo === 'OK' },
          ].map(s => (
            <div key={s.label} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><s.icon className="w-4 h-4 text-slate-400" /><span className="text-xs font-semibold text-slate-600">{s.label}</span></div>
              <div className="flex items-center gap-1.5">
                <span className={`text-[10px] font-bold ${s.ok ? 'text-[#a05e49]' : 'text-amber-600'}`}>{s.ok ? 'OK' : 'ALERTA'}</span>
                <div className={`w-3 h-3 rounded-full ${s.ok ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Alertas de riesgos */}
      {hasHighRisks && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-amber-500" />
            <div><p className="text-sm font-semibold text-amber-800 uppercase">Observaciones de Seguridad</p>
              <p className="text-xs text-amber-600">{a.riesgos.filter(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO').length} riesgo(s) alto(s)/critico(s) detectado(s)</p></div>
            <span className="ml-auto text-xs font-bold text-white bg-amber-500 px-2.5 py-1 rounded-full">{a.riesgos.filter(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO').length} alertas</span>
          </div>
          <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Riesgos Altos/Criticos ({a.riesgos.filter(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO').length})</p>
          <div className="space-y-2">
            {a.riesgos.filter(r => r.nivel === 'ALTO' || r.nivel === 'CRITICO').map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${RIESGO_BG[r.nivel]}`}>{r.nivel}</span>
                  <span className="text-sm text-slate-700">{r.descripcion}</span>
                </div>
                <span className="text-xs text-slate-400">P:{r.probabilidad} S:{r.severidad}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info General + Validacion */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Informacion General</h3>
          <div className="space-y-2">
            {[
              { l: 'Mandante', v: a.mandante }, { l: 'Empresa(s)', v: a.empresas?.length > 0 ? a.empresas.map(e => e.razon_social).join(', ') : (a.empresa_nombre || 'Lomas Bayas') },
              { l: 'Gerencia', v: a.gerencia_mandante || '-' },
              { l: 'Tipo Tarea', v: a.tipo_tarea, accent: true }, { l: 'Tipo Actividad', v: a.tipo_actividad },
              { l: 'Fecha Planificada', v: a.fecha_planificada?.split('-').reverse().join('/'), icon: Calendar },
              { l: 'Hora Inicio', v: a.hora_inicio || '-', icon: Clock }, { l: 'Hora Termino', v: a.hora_termino || '-', icon: Clock },
              { l: 'Duracion Estimada', v: a.duracion_horas ? `${a.duracion_horas}h` : '-' },
              { l: 'Ubicacion', v: a.ubicacion || '-', icon: MapPin },
              { l: 'Area', v: a.area || '-' }, { l: 'Sector', v: a.sitio || '-' },
            ].map(item => (
              <div key={item.l} className="flex items-center justify-between py-1">
                <span className="text-xs text-slate-400">{item.l}</span>
                <span className={`text-sm ${item.accent ? 'text-[#a05e49] font-medium' : 'text-slate-700'} flex items-center gap-1`}>
                  {item.icon && <item.icon className="w-3 h-3 text-slate-400" />}{item.v || '-'}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Resumen de Validacion</h3>
          {ssoTotal > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Preguntas SSO</span><span className="text-sm font-medium text-slate-700">{ssoAnswered}/{ssoTotal} respondidas</span></div>
              <div className="w-full bg-slate-100 rounded-full h-2"><div className={`h-2 rounded-full ${ssoAnswered === ssoTotal ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{width: `${(ssoAnswered/ssoTotal)*100}%`}} /></div>
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Personal</span><span className="text-sm text-slate-700">{a.personal?.length || 0} asignado(s)</span></div>
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Recursos</span><span className="text-sm text-slate-700">{a.recursos?.length || 0} asignado(s)</span></div>
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Riesgos</span><span className="text-sm text-slate-700">{a.riesgos?.length || 0} identificado(s)</span></div>
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Validacion Completa</span><span className={`text-sm font-medium ${ssoAnswered === ssoTotal && ssoTotal > 0 ? 'text-[#a05e49]' : 'text-red-500'}`}>{ssoAnswered === ssoTotal && ssoTotal > 0 ? 'Si' : 'No'}</span></div>
            </div>
          ) : <p className="text-sm text-slate-400 italic">Sin datos de validacion</p>}
        </div>
      </div>

      {/* Personal */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Personal Asignado ({a.personal?.length || 0})</h3></div>
        {a.personal?.length > 0 ? (
          <table className="w-full"><thead><tr className="border-b border-slate-100"><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">RUT</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">NOMBRE</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">ROL</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">EMPRESA</th><th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">SEMAFORO</th></tr></thead>
            <tbody>{a.personal.map(p => (
              <tr key={p.id} className="border-b border-slate-50"><td className="px-3 py-2.5 text-sm font-mono text-slate-600 whitespace-nowrap">{p.rut}</td><td className="px-3 py-2.5 text-sm text-slate-800">{p.nombre}</td><td className="px-3 py-2.5 text-sm text-slate-500">{p.rol}</td>
                <td className="px-3 py-2.5 text-sm text-slate-500">{p.empresa || 'Lomas Bayas'}</td>
                <td className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1 text-xs font-medium text-[#a05e49]"><span className="w-2 h-2 rounded-full bg-emerald-500" />OK</span></td></tr>
            ))}</tbody></table>
        ) : <p className="text-sm text-slate-400 italic">Sin personal asignado</p>}
      </div>

      {/* Recursos */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><Wrench className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Recursos Asignados ({a.recursos?.length || 0})</h3></div>
        {a.recursos?.length > 0 ? (
          <table className="w-full"><thead><tr className="border-b border-slate-100"><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">CODIGO</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">NOMBRE</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">TIPO</th><th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">EMPRESA</th><th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">SEMAFORO</th></tr></thead>
            <tbody>{a.recursos.map(r => (
              <tr key={r.id} className="border-b border-slate-50"><td className="px-3 py-2.5 text-sm font-mono text-slate-600">{r.codigo}</td><td className="px-3 py-2.5 text-sm text-slate-800">{r.nombre}</td>
                <td className="px-3 py-2.5 text-sm text-slate-500">{r.tipo || '-'}</td>
                <td className="px-3 py-2.5 text-sm text-slate-500">{r.empresa || 'Lomas Bayas'}</td>
                <td className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1 text-xs font-medium text-[#a05e49]"><span className="w-2 h-2 rounded-full bg-emerald-500" />OK</span></td></tr>
            ))}</tbody></table>
        ) : <p className="text-sm text-slate-400 italic">Sin recursos asignados</p>}
      </div>

      {/* Riesgos - separated by origin */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Riesgos Identificados ({a.riesgos?.length || 0})</h3></div>
        {a.riesgos?.length > 0 ? (
          <div className="space-y-4">
            {/* Riesgos del Area/Sector (catalogo) */}
            {a.riesgos.filter(r => r.origen === 'catalogo').length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-semibold text-[#1B919B] uppercase tracking-wider">Riesgos del Area / Sector</span>
                  <span className="text-[9px] bg-[#1B919B]/10 text-[#1B919B] px-2 py-0.5 rounded-full">{a.area}{a.sitio ? ` / ${a.sitio}` : ''}</span>
                </div>
                <div className="space-y-2">{a.riesgos.filter(r => r.origen === 'catalogo').map(r => (
                  <div key={r.id} className="p-3 bg-[#1B919B]/5 rounded-lg border border-[#1B919B]/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${RIESGO_BG[r.nivel]}`}>{r.nivel}</span><span className="text-sm text-slate-700 font-medium">{r.descripcion}</span></div>
                      <span className="text-xs text-slate-400">P:{r.probabilidad} S:{r.severidad}</span>
                    </div>
                    {r.medida_control && <p className="text-xs text-[#1B919B] mt-1.5 pl-1">Control: {r.medida_control}</p>}
                  </div>
                ))}</div>
              </div>
            )}
            {/* Riesgos propios de la Actividad */}
            {a.riesgos.filter(r => r.origen !== 'catalogo').length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-semibold text-[#a05e49] uppercase tracking-wider">Riesgos de la Actividad</span>
                </div>
                <div className="space-y-2">{a.riesgos.filter(r => r.origen !== 'catalogo').map(r => (
                  <div key={r.id} className="p-3 bg-[#a05e49]/5 rounded-lg border border-[#a05e49]/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${RIESGO_BG[r.nivel]}`}>{r.nivel}</span><span className="text-sm text-slate-700 font-medium">{r.descripcion}</span></div>
                      <span className="text-xs text-slate-400">P:{r.probabilidad} S:{r.severidad}</span>
                    </div>
                    {r.medida_control && <p className="text-xs text-[#a05e49] mt-1.5 pl-1">Control: {r.medida_control}</p>}
                  </div>
                ))}</div>
              </div>
            )}
            {/* If no origen field yet (old data), show all flat */}
            {a.riesgos.every(r => !r.origen) && (
              <div className="space-y-2">{a.riesgos.map(r => (
                <div key={r.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded text-white ${RIESGO_BG[r.nivel]}`}>{r.nivel}</span><span className="text-sm text-slate-700">{r.descripcion}</span></div>
                    <span className="text-xs text-slate-400">P:{r.probabilidad} S:{r.severidad}</span>
                  </div>
                  {r.medida_control && <p className="text-xs text-slate-400 mt-1.5 pl-1">Control: {r.medida_control}</p>}
                </div>
              ))}</div>
            )}
          </div>
        ) : <p className="text-sm text-slate-400 italic">Sin riesgos identificados</p>}
      </div>

      {/* Preguntas SSO */}
      {a.preguntas_sso?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4"><Shield className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Preguntas SSO ({ssoAnswered}/{ssoTotal})</h3></div>
          <div className="space-y-2">{a.preguntas_sso.map((q, i) => (
            <div key={q.id || i} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
              <span className="text-xs font-bold text-slate-400 mt-0.5">{i+1}.</span>
              <div className="flex-1"><p className="text-sm text-slate-700">{q.pregunta}</p>
                {q.observacion && <p className="text-xs text-red-500 mt-0.5">Obs: {q.observacion}</p>}</div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${q.respuesta === 'SI' ? 'bg-emerald-100 text-emerald-700' : q.respuesta === 'NO' ? 'bg-red-100 text-red-700' : q.respuesta === 'NA' ? 'bg-slate-100 text-slate-500' : 'bg-slate-50 text-slate-300'}`}>
                {q.respuesta || '-'}
              </span>
            </div>
          ))}</div>
        </div>
      )}

      {/* Historial */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><History className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-700">Historial de Aprobaciones</h3></div>
        {a.historial?.length > 0 ? (
          <div className="space-y-3">
            {a.historial.map(h => (
              <div key={h.id} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                  h.accion === 'APROBAR' ? 'bg-emerald-100' : h.accion === 'RECHAZAR' ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  {h.accion === 'APROBAR' ? <Check className="w-4 h-4 text-[#a05e49]" /> :
                   h.accion === 'RECHAZAR' ? <X className="w-4 h-4 text-red-600" /> :
                   <Clock className="w-4 h-4 text-blue-600" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{h.usuario_nombre}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                      h.accion === 'APROBAR' ? 'bg-emerald-100 text-emerald-700' : h.accion === 'RECHAZAR' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>{h.accion === 'ENVIAR_APROBACION' ? 'Enviada' : h.accion === 'APROBAR' ? 'Aprobada' : 'Rechazada'}</span>
                    <span className="text-xs text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString('es-CL') : ''}</span>
                  </div>
                  {h.comentario && <p className="text-sm text-slate-500 mt-0.5">{h.comentario}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 italic">Sin historial de aprobaciones</p>}
      </div>
    </div>
  );
}
